import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import './SaveRouteModal.css'; // Reuse the same styling

interface DownloadModalProps {
  open: boolean;
  onClose?: () => void;
  title?: string;
  message?: string;
  allowClose?: boolean;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  open,
  onClose,
  title = 'Generating GPX File',
  message = 'Please wait while we generate your route file...',
  allowClose = false
}) => {
  const handleClose = () => {
    if (allowClose && onClose) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={allowClose ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      className="save-route-modal"
      disableEscapeKeyDown={!allowClose}
      disableRestoreFocus={true}
    >
      <DialogTitle className="save-route-modal__title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          {allowClose && onClose && (
            <IconButton 
              onClick={handleClose}
              size="small"
              className="save-route-modal__close-button"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent className="save-route-modal__content">
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 4,
            gap: 3
          }}
        >
          {/* Loading Icon */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{ color: 'var(--color-accent)' }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DownloadIcon 
                sx={{ 
                  color: 'var(--color-accent)', 
                  fontSize: '1.5rem' 
                }} 
              />
            </Box>
          </Box>

          {/* Loading Message */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'var(--color-dark)',
                fontWeight: 500,
                mb: 1
              }}
            >
              {message}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'var(--color-dark-gray)',
                fontSize: '0.875rem'
              }}
            >
              This may take a few moments for complex routes
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadModal;
